import {useEffect, useRef} from 'react'

import {
  DockedPanel,
  useActiveDockedPanel,
  useDockedPanelMetadata,
  useDockedPanelOpenRevision,
} from '../../../contexts/DockedPanelContext'
import {useManageBatchOpenTelemetry} from './manage-batch-telemetry'

/**
 * Emits `unified_batch_panel.open` once per deliberate open of the Manage batch
 * panel, reading its entry surface from the panel metadata the opener passed to
 * `openPanel`.
 *
 * Renders nothing and is mounted once by `DockedSidePanelContent`, which stays
 * mounted for the lifetime of the page. Centralising the event here (rather
 * than at each opener, which historically missed the per-comment
 * `Add to batch` path entirely and under-counted opens ~3x) means:
 *  - a new entry point cannot open the panel without being counted;
 *  - clicking an opener while the panel is already open cannot double-count,
 *    because the event keys off the open itself, not the click.
 *
 * Keyed off `openRevision` rather than any derived visibility state, because
 * neither `activePanel` nor `renderActive` isolates a real open:
 *  - `renderActive` flips while the container is resized, which would re-emit
 *    for a panel the user never re-opened;
 *  - `activePanel` is unchanged when the panel is re-opened after being hidden
 *    for resize (missing that open), and the provider masks it to `null` off a
 *    supported route, so a route round-trip would emit a phantom open with the
 *    previous entry surface.
 *
 * Initialising the ref from the current revision means a remount never emits.
 */
export function ManageBatchOpenTelemetry() {
  const activePanel = useActiveDockedPanel()
  const metadata = useDockedPanelMetadata()
  const openRevision = useDockedPanelOpenRevision()
  const emitPanelOpen = useManageBatchOpenTelemetry()

  const entrySurface = metadata?.manageBatchEntrySurface
  const lastRevisionRef = useRef(openRevision)

  useEffect(() => {
    if (openRevision === lastRevisionRef.current) return
    lastRevisionRef.current = openRevision

    // The revision covers every panel, so ignore opens of the other panels.
    if (activePanel !== DockedPanel.ManageBatchReviewComments) return

    // Opens that carry no metadata (today only the `panel` URL-param restore)
    // are still counted, so `open` stays a valid funnel top.
    emitPanelOpen(entrySurface ?? 'unknown')
  }, [activePanel, emitPanelOpen, entrySurface, openRevision])

  return null
}
